import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { db } from './firebase';

const USERS_COLLECTION = 'usuarios';
const MEMBERS_COLLECTION = 'jogador_turma';
const RELATION_COLLECTION = 'usuarios_relacionamentos';
const INVITES_COLLECTION = 'turma_convites';
const DEFAULT_PROFILE_FIELDS = {
  nickname: '',
  birth: '',
  altura: '',
  peso: '',
  posicao: '',
  qt_partidas: 0,
  gols: 0,
  assistencias: 0,
  vitorias: 0,
  derrotas: 0,
  empates: 0,
  jogosSemTomarGol: 0,
  influenciaVitoria: 0,
  overall: 65,
  status: ''
};

export const upsertUserProfile = async (user) => {
  if (!user) return;
  const docRef = doc(db, USERS_COLLECTION, user.uid);
  const payload = {
    id: user.uid,
    displayName: user.displayName || '',
    email: (user.email || '').toLowerCase(),
    photoURL: user.photoURL || '',
    updated_at: serverTimestamp()
  };

  const snapshot = await getDoc(docRef);
  if (snapshot.exists()) {
    await setDoc(docRef, payload, { merge: true });
  } else {
    await setDoc(docRef, {
      ...payload,
      ...DEFAULT_PROFILE_FIELDS,
      overall: 65,
      created_at: serverTimestamp()
    });
  }
};

export const findUserByEmail = async (email) => {
  if (!email) return null;
  const normalized = email.toLowerCase();
  const usersQuery = query(
    collection(db, USERS_COLLECTION),
    where('email', '==', normalized)
  );
  const snapshot = await getDocs(usersQuery);
  if (snapshot.empty) {
    return null;
  }
  return snapshot.docs[0].data();
};

export const getUserProfile = async (userId) => {
  if (!userId) return null;
  const docRef = doc(db, USERS_COLLECTION, userId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) {
    return null;
  }
  return {
    ...DEFAULT_PROFILE_FIELDS,
    ...snapshot.data()
  };
};

export const updateUserProfileFields = async (userId, fields) => {
  if (!userId || !fields) return;
  const docRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(docRef, fields, { merge: true });
};

const deleteDocsWithBackup = async (collectionName, constraints = []) => {
  if (!collectionName || !constraints.length) {
    return null;
  }
  const collectionRef = collection(db, collectionName);
  const docsQuery = query(collectionRef, ...constraints);
  const snapshot = await getDocs(docsQuery);
  if (snapshot.empty) {
    return null;
  }

  const backups = snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    data: docSnapshot.data()
  }));

  await Promise.all(
    backups.map(({ id }) => deleteDoc(doc(db, collectionName, id)))
  );

  return async () => {
    await Promise.all(
      backups.map(({ id, data }) => setDoc(doc(db, collectionName, id), data))
    );
  };
};

export const deleteUserAccountData = async (userId) => {
  if (!userId) {
    return async () => {};
  }

  const restoreActions = [];
  const userDocRef = doc(db, USERS_COLLECTION, userId);
  const userSnapshot = await getDoc(userDocRef);

  if (userSnapshot.exists()) {
    const userData = userSnapshot.data();
    await deleteDoc(userDocRef);
    restoreActions.push(() => setDoc(userDocRef, userData));
  }

  const membershipRestore = await deleteDocsWithBackup(MEMBERS_COLLECTION, [
    where('id_usuario', '==', userId)
  ]);
  if (membershipRestore) {
    restoreActions.push(membershipRestore);
  }

  const outgoingRelationsRestore = await deleteDocsWithBackup(RELATION_COLLECTION, [
    where('id_user_solicitante', '==', userId)
  ]);
  if (outgoingRelationsRestore) {
    restoreActions.push(outgoingRelationsRestore);
  }

  const incomingRelationsRestore = await deleteDocsWithBackup(RELATION_COLLECTION, [
    where('id_user_receptor', '==', userId)
  ]);
  if (incomingRelationsRestore) {
    restoreActions.push(incomingRelationsRestore);
  }

  const invitesReceivedRestore = await deleteDocsWithBackup(INVITES_COLLECTION, [
    where('id_usuario_convidado', '==', userId)
  ]);
  if (invitesReceivedRestore) {
    restoreActions.push(invitesReceivedRestore);
  }

  const invitesSentRestore = await deleteDocsWithBackup(INVITES_COLLECTION, [
    where('id_usuario_convite', '==', userId)
  ]);
  if (invitesSentRestore) {
    restoreActions.push(invitesSentRestore);
  }

  return async () => {
    for (let index = restoreActions.length - 1; index >= 0; index -= 1) {
      const restoreAction = restoreActions[index];
      await restoreAction();
    }
  };
};
